import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mail, ImagePlus, X } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = [
  { value: "undecided", label: "Not sure yet — help me choose" },
  { value: "9x12", label: '9" × 12" — The Keepsake · $100' },
  { value: "14x18", label: '14" × 18" — The Portrait · $175' },
  { value: "26x30", label: '26" × 30" — The Masterpiece · $350' },
];

const MAX_PHOTOS = 6;
const MAX_BYTES = 10 * 1024 * 1024;

const inputCls = "w-full px-4 py-3 rounded-xl bg-[#0C0A09] border border-[#2E2721] text-stone-200 text-sm placeholder:text-stone-600 transition-shadow duration-300";

export const ConsultForm = ({ selectedSize }) => {
  const [form, setForm] = useState({ name: "", email: "", pet_name: "", size: "undecided", background: "", accents: "", notes: "" });
  const [photos, setPhotos] = useState([]);
  const [phase, setPhase] = useState("idle"); // idle | uploading | sending
  const fileRef = useRef(null);

  useEffect(() => {
    if (selectedSize) setForm((f) => ({ ...f, size: selectedSize }));
  }, [selectedSize]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhotos = (fileList) => {
    const incoming = Array.from(fileList || []);
    const accepted = [];
    for (const f of incoming) {
      if (photos.length + accepted.length >= MAX_PHOTOS) {
        toast.error(`Up to ${MAX_PHOTOS} photos`, { description: "That is plenty for the consultation." });
        break;
      }
      if (!f.type.startsWith("image/")) {
        toast.error("Images only", { description: `${f.name} is not an image file.` });
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error("Photo too large", { description: `${f.name} is over 10MB.` });
        continue;
      }
      accepted.push({ file: f, url: URL.createObjectURL(f), name: f.name });
    }
    if (accepted.length) setPhotos((p) => [...p, ...accepted]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (i) => {
    setPhotos((p) => {
      URL.revokeObjectURL(p[i].url);
      return p.filter((_, j) => j !== i);
    });
  };

  const resetPhotos = () => {
    photos.forEach((p) => URL.revokeObjectURL(p.url));
    setPhotos([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      let photo_paths = [];
      if (photos.length) {
        setPhase("uploading");
        for (const p of photos) {
          const fd = new FormData();
          fd.append("file", p.file);
          const r = await axios.post(`${API}/upload`, fd);
          photo_paths.push(r.data.path);
        }
      }
      setPhase("sending");
      await axios.post(`${API}/consult`, { ...form, photo_paths });
      toast.success("Your consultation request is on its way", {
        description: "Expect a personal reply from the studio soon.",
      });
      setForm({ name: "", email: "", pet_name: "", size: "undecided", background: "", accents: "", notes: "" });
      resetPhotos();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Something went wrong", {
        description: typeof detail === "string" ? detail : "Please try again, or email info@petraits.net directly.",
      });
    } finally {
      setPhase("idle");
    }
  };

  const busy = phase !== "idle";
  const buttonLabel = phase === "uploading" ? "Uploading photos…" : phase === "sending" ? "Sending…" : "Send Consultation Request";

  return (
    <section id="consult" data-testid="consult-section" className="relative py-28 sm:py-36 bg-[#100D0A] overflow-hidden">
      <div className="absolute inset-0 spotlight" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2">
          <Reveal><Eyebrow>Begin the Conversation</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-serif tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-5xl text-[#EAF5FA]">
              Request your <span className="gold-foil-text italic">consultation</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base text-stone-300 leading-relaxed">
              Tell me about your companion and what you have in mind. This is the first brushstroke —
              a real, personal reply follows every request.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 space-y-4 text-sm text-stone-400">
              <p className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#41C2F2] shrink-0" />
                <a data-testid="consult-email-link" href="mailto:info@petraits.net" className="hover:text-[#41C2F2] transition-colors">info@petraits.net</a>
              </p>
              <p className="leading-relaxed border-l border-[#41C2F2]/30 pl-4">
                Attach one clear headshot and as many candid shots as you like — the more personality I can see, the truer the painting.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-3">
          <form
            data-testid="consultation-form"
            onSubmit={submit}
            className="p-7 sm:p-10 rounded-2xl bg-[#1F1B17] border border-[#2E2721] space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="consult-name" className="block text-xs uppercase tracking-[0.2em] font-mono text-stone-500 mb-2">Your name *</label>
                <input id="consult-name" data-testid="consultation-input-name" required value={form.name} onChange={set("name")} placeholder="Jane Appleseed" className={inputCls} maxLength={120} />
              </div>
              <div>
                <label htmlFor="consult-email" className="block text-xs uppercase tracking-[0.2em] font-mono text-stone-500 mb-2">Email *</label>
                <input id="consult-email" data-testid="consultation-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" className={inputCls} maxLength={120} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="consult-pet" className="block text-xs uppercase tracking-[0.2em] font-mono text-stone-500 mb-2">Pet's name(s)</label>
                <input id="consult-pet" data-testid="consultation-input-petname" value={form.pet_name} onChange={set("pet_name")} placeholder="Biscuit" className={inputCls} maxLength={120} />
              </div>
              <div>
                <label htmlFor="consult-size" className="block text-xs uppercase tracking-[0.2em] font-mono text-stone-500 mb-2">Portrait size</label>
                <select id="consult-size" data-testid="consultation-select-size" value={form.size} onChange={set("size")} className={inputCls}>
                  {SIZES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="consult-bg" className="block text-xs uppercase tracking-[0.2em] font-mono text-stone-500 mb-2">Background color ideas</label>
                <input id="consult-bg" data-testid="consultation-input-background" value={form.background} onChange={set("background")} placeholder="Deep forest green…" className={inputCls} maxLength={120} />
              </div>
              <div>
                <label htmlFor="consult-accents" className="block text-xs uppercase tracking-[0.2em] font-mono text-stone-500 mb-2">Accent color ideas</label>
                <input id="consult-accents" data-testid="consultation-input-accents" value={form.accents} onChange={set("accents")} placeholder="Gold, dusty rose…" className={inputCls} maxLength={160} />
              </div>
            </div>

            <div>
              <span className="block text-xs uppercase tracking-[0.2em] font-mono text-stone-500 mb-2">Pet photos</span>
              <input
                ref={fileRef}
                data-testid="consultation-input-photos"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => addPhotos(e.target.files)}
              />
              <button
                type="button"
                data-testid="consultation-photo-dropzone"
                onClick={() => fileRef.current?.click()}
                className="w-full px-4 py-6 rounded-xl border border-dashed border-[#2E2721] hover:border-[#41C2F2]/60 bg-[#0C0A09] text-stone-400 hover:text-[#41C2F2] transition-colors duration-300 flex flex-col items-center gap-2"
              >
                <ImagePlus className="w-6 h-6" />
                <span className="text-sm">Add photos — one headshot + candid shots</span>
                <span className="text-xs text-stone-600">JPG, PNG or WEBP · up to {MAX_PHOTOS} photos · 10MB each</span>
              </button>
              {photos.length > 0 && (
                <div data-testid="consultation-photo-previews" className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {photos.map((p, i) => (
                    <div key={p.url} className="relative group aspect-square rounded-lg overflow-hidden border border-[#2E2721]">
                      <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        data-testid={`consultation-photo-remove-${i + 1}`}
                        onClick={() => removePhoto(i)}
                        aria-label={`Remove ${p.name}`}
                        className="absolute top-1 right-1 p-1 rounded-full bg-[#0C0A09]/80 text-stone-300 hover:text-[#C85A32] opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="consult-notes" className="block text-xs uppercase tracking-[0.2em] font-mono text-stone-500 mb-2">Their story</label>
              <textarea id="consult-notes" data-testid="consultation-input-notes" rows={4} value={form.notes} onChange={set("notes")} placeholder="Tell me about them — personality, habits, the look they give you…" className={`${inputCls} resize-none`} maxLength={2000} />
            </div>
            <button
              data-testid="consultation-submit-button"
              type="submit"
              disabled={busy}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-10 py-4 rounded-full bg-[#41C2F2] text-[#0C0A09] font-semibold text-sm tracking-wide hover:bg-[#BDE9FA] disabled:opacity-60 transition-colors duration-300"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {buttonLabel}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
